/**
 * Sistemas de vantagem mecânica (redução de força): a talha de `razao`
 * trechos entre um bloco fixo, preso à ancoragem, e um bloco móvel, preso à
 * carga. Puxar a ponta livre `razao` metros move a carga 1 metro, e a força
 * necessária cai na mesma proporção, menos o atrito das roldanas.
 *
 * TypeScript puro: sem React e sem Three.js. A cena 3D desenha a disposição
 * de `disposicaoDoSistema`, e o painel mostra o cálculo de `analisarSistemaVM`.
 */

import { analisarFusivel, type AnaliseDoFusivel } from './fusivelSistema'

export const RAZOES_VANTAGEM = [2, 3, 4, 5, 6] as const
export type RazaoVantagem = (typeof RAZOES_VANTAGEM)[number]

/** Eficiência de cada roldana: ~92% é um valor didático de roldana com rolamento, não de catálogo. */
export const EFICIENCIA_POR_ROLDANA_PADRAO = 0.92

const prender = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

/** A razão inteira mais próxima, dentro de 2 a 6. */
export const razaoValida = (valor: number): RazaoVantagem =>
  prender(Math.round(Number.isFinite(valor) ? valor : 3), 2, 6) as RazaoVantagem

export interface ComposicaoDoSistema {
  readonly razao: RazaoVantagem
  /** Roldanas no bloco móvel (na carga). */
  readonly roldanasMoveis: number
  /** Roldanas no bloco fixo (na ancoragem), contando a de saída da ponta livre. */
  readonly roldanasFixas: number
  /** Onde a ponta de trás da corda está presa: razões pares no bloco fixo, ímpares no móvel. */
  readonly pontaMortaNo: 'fixo' | 'movel'
  /** O nome que se dá ao arranjo, para o painel. */
  readonly nome: string
}

const NOMES: Readonly<Record<RazaoVantagem, string>> = {
  2: '2:1 simples',
  3: '3:1 em Z',
  4: '4:1 (duas roldanas móveis)',
  5: '5:1 (duas roldanas móveis)',
  6: '6:1 (três roldanas móveis)',
}

/**
 * Cada trecho entre os blocos é uma volta de corda; a ponta livre sai do bloco
 * fixo. Com `razao` trechos: metade (arredondada para baixo) das roldanas fica
 * no bloco móvel e o resto no fixo. Razão par começa no bloco fixo, ímpar no
 * móvel — é o que faz a ponta livre sair sempre do fixo, onde se puxa sem
 * somar trecho.
 */
export function composicaoDoSistema(razaoBruta: number): ComposicaoDoSistema {
  const razao = razaoValida(razaoBruta)
  return {
    razao,
    roldanasMoveis: Math.floor(razao / 2),
    roldanasFixas: Math.ceil(razao / 2),
    pontaMortaNo: razao % 2 === 0 ? 'fixo' : 'movel',
    nome: NOMES[razao],
  }
}

export interface EntradaSistemaVM {
  readonly razao: number
  /** Peso a levantar, em kgf. */
  readonly cargaKgf: number
  /** Eficiência de cada roldana, de 0 a 1 (1 = sem atrito). */
  readonly eficienciaPorRoldana?: number
  /** O fusível do sistema: 0 sem, 1 Prusik, 2 clássico, 3 e 4 freio oito (ver `fusivelSistema.ts`). */
  readonly fusivel?: number
}

export interface ResultadoSistemaVM {
  readonly razao: RazaoVantagem
  /** A vantagem sem atrito: igual à razão. */
  readonly vantagemIdeal: number
  /** A vantagem com o atrito das roldanas. */
  readonly vantagemReal: number
  /** Força na ponta livre sem atrito, em kgf. */
  readonly forcaIdealKgf: number
  /** Força na ponta livre com o atrito, em kgf. */
  readonly forcaRealKgf: number
  /** Quanto da força se perde no atrito, como fração da força ideal (0,2 = precisa de 20% a mais). */
  readonly acrescimoPeloAtrito: number
  /** Quantos metros de corda se puxam para a carga subir 1 m. */
  readonly cordaPorMetroDeCarga: number
  /** Tensão em cada trecho, do mais perto da ponta livre ao mais perto da ponta morta, em kgf. */
  readonly tensoesPorTrechoKgf: readonly number[]
  /** Força na ancoragem do bloco fixo: a carga menos a força de puxada, porque a ponta livre sai para o lado oposto da carga e ajuda a segurar. */
  readonly cargaNaAncoragemKgf: number
  /** O fusível numa carga travada; `null` quando o sistema não tem fusível. */
  readonly fusivel: AnaliseDoFusivel | null
}

/**
 * Cada roldana que a corda contorna custa um pouco de atrito: o lado que se
 * puxa tem `1/η` da tensão do outro. Da ponta livre para a ponta morta a
 * tensão cai `η` por roldana, então os trechos valem `F·η, F·η², …, F·ηⁿ` e a
 * carga é a soma deles:
 *
 *   W = F · η·(1 − ηⁿ)/(1 − η)  ⇒  vantagem real = η·(1 − ηⁿ)/(1 − η)
 *
 * Com η = 1 isto é `n`, a vantagem ideal. A corda sai do bloco fixo, então a
 * ponta livre não soma trecho, e a força na ancoragem é a carga menos a força
 * de puxada. Aqui a ponta livre sai para o lado OPOSTO da carga, então ela
 * ajuda a segurar: a ancoragem sustenta `W − F` (no 2:1 ideal, metade da
 * carga; no 3:1, dois terços). Se a corda fosse desviada de volta para o lado
 * da carga, a ancoragem sustentaria `W + F`.
 */
export function analisarSistemaVM(entrada: EntradaSistemaVM): ResultadoSistemaVM {
  const razao = razaoValida(entrada.razao)
  const eta = prender(entrada.eficienciaPorRoldana ?? EFICIENCIA_POR_ROLDANA_PADRAO, 0.01, 1)
  const carga = Math.max(0, entrada.cargaKgf)
  const vantagemReal = eta === 1 ? razao : (eta * (1 - eta ** razao)) / (1 - eta)
  const forcaIdeal = carga / razao
  const forcaReal = carga / vantagemReal
  return {
    razao,
    vantagemIdeal: razao,
    vantagemReal,
    forcaIdealKgf: forcaIdeal,
    forcaRealKgf: forcaReal,
    acrescimoPeloAtrito: forcaReal / forcaIdeal - 1,
    cordaPorMetroDeCarga: razao,
    tensoesPorTrechoKgf: Array.from({ length: razao }, (_, i) => forcaReal * eta ** (i + 1)),
    cargaNaAncoragemKgf: carga - forcaReal,
    fusivel: entrada.fusivel ? analisarFusivel(entrada.fusivel, vantagemReal, carga) : null,
  }
}

// ---------------------------------------------------------------------------
// Disposição para o desenho
// ---------------------------------------------------------------------------

/** Distância entre trechos vizinhos, que é o diâmetro da roldana, em metros. */
export const ESPACO_ENTRE_TRECHOS_M = 0.12

export interface TrechoDoSistema {
  readonly indice: number
  /** Posição (x, z) de onde o trecho sai e para onde vai, no referencial do sistema. */
  readonly de: readonly [number, number]
  readonly para: readonly [number, number]
  /** O trecho puxa o bloco móvel para o bloco fixo, sempre: -1 em X. */
  readonly sentidoDaForca: -1
}

export interface RoldanaDoSistema {
  readonly bloco: 'fixo' | 'movel'
  readonly x: number
  readonly z: number
}

export interface DisposicaoDoSistema {
  readonly composicao: ComposicaoDoSistema
  readonly trechos: readonly TrechoDoSistema[]
  readonly roldanas: readonly RoldanaDoSistema[]
  /** Onde a ponta de trás da corda se prende. */
  readonly pontaMorta: { readonly bloco: 'fixo' | 'movel'; readonly x: number; readonly z: number }
  /** De onde a ponta livre sai, e até onde se desenha. */
  readonly pontaLivre: { readonly x: number; readonly z: number; readonly ateX: number }
  /** Faixa em Z ocupada por cada bloco, para desenhar a carcaça. */
  readonly faixaFixo: readonly [number, number]
  readonly faixaMovel: readonly [number, number]
}

/**
 * Onde cada trecho, roldana e ponta fica. O bloco fixo está em `x = 0` e o
 * móvel em `x = cursoM`; os trechos correm em X, lado a lado em Z. A ponta
 * livre sai do bloco fixo para o lado negativo de X (para longe da carga).
 */
export function disposicaoDoSistema(razaoBruta: number, cursoM: number): DisposicaoDoSistema {
  const composicao = composicaoDoSistema(razaoBruta)
  const n = composicao.razao
  const d = ESPACO_ENTRE_TRECHOS_M
  const zDoTrecho = (k: number) => (k - n / 2) * d
  const comecaNoFixo = composicao.pontaMortaNo === 'fixo'

  const trechos: TrechoDoSistema[] = []
  const roldanas: RoldanaDoSistema[] = []
  for (let k = 0; k < n; k += 1) {
    // trechos pares saem do bloco em que a corda começa; os ímpares voltam
    const saiDoFixo = comecaNoFixo ? k % 2 === 0 : k % 2 === 1
    const xDe = saiDoFixo ? 0 : cursoM
    const xPara = saiDoFixo ? cursoM : 0
    trechos.push({ indice: k, de: [xDe, zDoTrecho(k)], para: [xPara, zDoTrecho(k)], sentidoDaForca: -1 })
    // a corda contorna uma roldana onde chega, ligando este trecho ao seguinte (ou à ponta livre)
    roldanas.push({ bloco: xPara === 0 ? 'fixo' : 'movel', x: xPara, z: (zDoTrecho(k) + zDoTrecho(k + 1)) / 2 })
  }

  const zDe = (bloco: 'fixo' | 'movel') => roldanas.filter((r) => r.bloco === bloco).map((r) => r.z)
  const faixa = (bloco: 'fixo' | 'movel'): readonly [number, number] => {
    const zs = zDe(bloco)
    return zs.length === 0 ? [0, 0] : [Math.min(...zs) - d / 2, Math.max(...zs) + d / 2]
  }
  return {
    composicao,
    trechos,
    roldanas,
    pontaMorta: { bloco: comecaNoFixo ? 'fixo' : 'movel', x: comecaNoFixo ? 0 : cursoM, z: zDoTrecho(0) },
    pontaLivre: { x: 0, z: zDoTrecho(n), ateX: -0.7 },
    faixaFixo: faixa('fixo'),
    faixaMovel: faixa('movel'),
  }
}
